import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  Button,
  IconChair2,
  IconRabbit,
  IconStopwatch,
  IconUserMultipleCircle,
  Input,
  Modal,
  ScreenLayout,
  StatusBadge,
  usePref,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  bookingToVisit,
  todayIso,
  visitStatusOf,
  OPEN_DAYS,
  OPEN_HOUR_END,
  OPEN_HOUR_START,
  SEAT_STOOLS,
  SEAT_TABLES,
  type AppTab,
  type SeatKind,
  type Visit,
  type VisitStatus,
} from './data.js';
import {
  DEFAULT_FILTERS,
  DEFAULT_VISIT_STATS,
  FILTERS_PREF_KEY,
  RANGE_OPTIONS,
  STATS_MAX,
  VISITS_STATS_PREF_KEY,
  VISIT_STAT_DEFS,
  visitFilterPrefId,
  type RangeId,
  type VisitStatId,
} from './settings-defs.js';
import { ReservationsSkeleton } from './skeletons.js';

/**
 * Ecran "Les visites" de Cat Manager Six.
 *
 * L'app LIT les reservations via `sdk.bookings.list()` et en CREE via
 * `sdk.bookings.create()` (une visite = un booking de type 'visit'). Elle
 * affiche une jauge de capacite du bar (6 tables, 5 postes en table haute) et
 * une bascule Aujourd'hui / Cette semaine, bornee aux jours ouvres (lundi a
 * vendredi) et aux heures d'ouverture (10h a 18h). Marquer une visite
 * arrivee / annulee demande un update du socle (non disponible en v0).
 *
 * Squelette 100% `ScreenLayout` (gabarit du socle), data 100% `@taki/sdk`,
 * UI 100% `@taki/ui`, zero couleur en dur.
 *
 * Reglages : ils vivent dans settings.tsx (hub Parametres du shell) ; l'ecran
 * LIT les prefs partagees ("app.cat-manager-six.filters" et
 * "app.cat-manager-six.stats.visits") via `usePref` et se resynchronise en direct.
 */

export interface ReservationsScreenProps {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  /** Demande d'ouverture du Modal de reservation (action du dock relayee par la racine). */
  openFormRequested: boolean;
  /** Acquitte la demande une fois le Modal ouvert (la racine la remet a zero). */
  onOpenFormHandled: () => void;
}

/** Un creneau tombe-t-il dans les jours ouvres et les heures d'ouverture. */
function isOpenSlot(date: Date): boolean {
  const openDays = OPEN_DAYS as readonly number[];
  const hour = date.getHours();
  return openDays.includes(date.getDay()) && hour >= OPEN_HOUR_START && hour < OPEN_HOUR_END;
}

/** Deux dates tombent-elles le meme jour calendaire. */
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Bornes lundi 00:00 -> vendredi 23:59 de la semaine du jour fourni. */
function weekBounds(now: Date): { start: Date; end: Date } {
  // getDay(): 0 = dimanche ... 6 = samedi. Jours ecoules depuis lundi.
  const daysSinceMonday = (now.getDay() + 6) % 7;
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - daysSinceMonday,
    0,
    0,
    0,
    0,
  );
  const end = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 4,
    23,
    59,
    59,
    999,
  );
  return { start, end };
}

/** Heure formatee HH:mm (creneau lisible), sans dependance externe. */
function formatTime(date: Date | null): string {
  if (date === null) return '--:--';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Libelle du creneau (debut -> fin) d'une visite. */
function formatSlot(visit: Visit): string {
  if (visit.startAt === null && visit.endAt === null) return 'Horaire non renseigne';
  if (visit.endAt === null) return formatTime(visit.startAt);
  return `${formatTime(visit.startAt)} a ${formatTime(visit.endAt)}`;
}

/** Libelle du jour d'un creneau (ex. "lun. 14/07"). */
function formatDay(date: Date | null): string {
  if (date === null) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const names = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
  return `${names[date.getDay()]} ${day}/${month}`;
}

/** Options de place pour le formulaire de creation. */
const SEAT_OPTIONS: { value: SeatKind; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'stool', label: 'Poste' },
];

export function ReservationsScreen({
  activeTab,
  onTabChange,
  openFormRequested,
  onOpenFormHandled,
}: ReservationsScreenProps): JSX.Element {
  const { sdk, config } = useTakiApp();

  const [visits, setVisits] = useState<Visit[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeId>('today');

  // Prefs partagees avec settings.tsx (hub Parametres du shell) : visibilite
  // des pastilles de periode (zone 4) et stats d'en-tete affichees (zone 3).
  const [filterVisibility] = usePref<Record<string, boolean>>(
    FILTERS_PREF_KEY,
    DEFAULT_FILTERS,
  );
  const [statIds] = usePref<string[]>(VISITS_STATS_PREF_KEY, DEFAULT_VISIT_STATS);

  // Formulaire de nouvelle reservation.
  const [showForm, setShowForm] = useState(false);
  const [formDate, setFormDate] = useState(todayIso);
  const [formTime, setFormTime] = useState('10:00');
  const [formSize, setFormSize] = useState('1');
  const [formSeat, setFormSeat] = useState<SeatKind>('table');
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Marquage arrivee / annulee d'une visite.
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const list = await sdk.bookings.list();
      setVisits(list.filter((booking) => booking.type === 'visit').map(bookingToVisit));
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Echec du chargement.');
    } finally {
      setLoadingList(false);
    }
  }, [sdk]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Action du dock relayee par l'ecran racine : ouvrir le Modal de reservation
  // puis acquitter (la demande repasse a false, l'effet ne rejoue pas).
  useEffect(() => {
    if (openFormRequested) {
      setShowForm(true);
      onOpenFormHandled();
    }
  }, [openFormRequested, onOpenFormHandled]);

  // Visites de la periode selectionnee, bornees lundi-vendredi 10h-18h, triees
  // par heure de debut. Filtrage COTE CLIENT (perimetre v0).
  const filtered = useMemo(() => {
    const now = new Date();
    const { start, end } = weekBounds(now);
    return visits
      .filter((visit) => {
        const slot = visit.startAt;
        if (slot === null) return false;
        if (!isOpenSlot(slot)) return false;
        if (range === 'today') return isSameDay(slot, now);
        return slot.getTime() >= start.getTime() && slot.getTime() <= end.getTime();
      })
      .sort((a, b) => (a.startAt?.getTime() ?? 0) - (b.startAt?.getTime() ?? 0));
  }, [visits, range]);

  // Compte les visites d'un jour calendaire donne pour un type de place.
  // Une visite annulee ne consomme plus de place.
  const countForDay = useCallback(
    (dayIso: string, seat: SeatKind): number => {
      const target = new Date(`${dayIso}T00:00:00`);
      if (Number.isNaN(target.getTime())) return 0;
      return visits.filter(
        (visit) =>
          visit.startAt !== null &&
          visit.seatKind === seat &&
          visit.status !== 'cancelled' &&
          isSameDay(visit.startAt, target),
      ).length;
    },
    [visits],
  );

  /** Marque une visite arrivee ou annulee (champ natif `status` du booking). */
  const handleVisitStatus = useCallback(
    async (visitId: string, next: VisitStatus) => {
      setStatusSavingId(visitId);
      setStatusError(null);
      try {
        await sdk.bookings.update(visitId, { status: next });
        await refresh();
      } catch (err) {
        setStatusError(err instanceof Error ? err.message : 'Echec du changement de statut.');
      } finally {
        setStatusSavingId(null);
      }
    },
    [sdk, refresh],
  );

  // Jauge du jour selectionne (aujourd'hui). En vue semaine, la jauge n'a pas
  // de sens (on compare a une capacite instantanee), on ne l'affiche donc pas.
  const tablesUsed = useMemo(() => countForDay(todayIso(), 'table'), [countForDay]);
  const stoolsUsed = useMemo(() => countForDay(todayIso(), 'stool'), [countForDay]);

  const seatState = (used: number, cap: number): { tone: 'success' | 'accent'; label: string } =>
    used >= cap ? { tone: 'accent', label: 'Complet' } : { tone: 'success', label: 'Disponible' };

  const tablesMeta = seatState(tablesUsed, SEAT_TABLES);
  const stoolsMeta = seatState(stoolsUsed, SEAT_STOOLS);
  const overflow = tablesUsed > SEAT_TABLES || stoolsUsed > SEAT_STOOLS;

  // Pastilles de periode reellement passees a la zone 4 : les visibles selon
  // la pref (cle absente = visible, seul un `false` explicite masque).
  const rangeFilters = useMemo(
    () =>
      RANGE_OPTIONS.filter(
        (option) => filterVisibility[visitFilterPrefId(option.id)] !== false,
      ).map((option) => ({ id: option.id, label: option.label })),
    [filterVisibility],
  );

  // Si la pastille ACTIVE vient d'etre masquee dans les reglages, l'ecran
  // rebascule sur la premiere periode visible (jamais de periode active invisible).
  useEffect(() => {
    if (filterVisibility[visitFilterPrefId(range)] === false) {
      const fallback = RANGE_OPTIONS.find(
        (option) => filterVisibility[visitFilterPrefId(option.id)] !== false,
      );
      if (fallback != null && fallback.id !== range) setRange(fallback.id);
    }
  }, [filterVisibility, range]);

  // Valeurs de TOUTES les stats du catalogue, calculees des donnees reelles.
  // La pref ne choisit que LESQUELLES afficher, jamais les valeurs.
  const statValues = useMemo<Record<VisitStatId, string>>(() => {
    const arrived = filtered.filter((visit) => visit.status === 'arrived').length;
    const cancelled = filtered.filter((visit) => visit.status === 'cancelled').length;
    return {
      period: String(filtered.length),
      arrived: String(arrived),
      cancelled: String(cancelled),
      'tables-free': String(Math.max(0, SEAT_TABLES - tablesUsed)),
    };
  }, [filtered, tablesUsed]);

  // Stats de la zone 3 : la selection de la pref, dans l'ordre du catalogue,
  // plafonnee a STATS_MAX (garde-fou si la pref stockee depasse le contrat).
  const headerStats = useMemo(
    () =>
      VISIT_STAT_DEFS.filter((def) => statIds.includes(def.id))
        .slice(0, STATS_MAX)
        .map((def) => ({ value: statValues[def.id], label: def.label })),
    [statIds, statValues],
  );

  const handleSaveVisit = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const start = new Date(`${formDate}T${formTime}:00`);
      if (Number.isNaN(start.getTime())) {
        setSaveError('Date ou heure invalide.');
        return;
      }
      if (!(OPEN_DAYS as readonly number[]).includes(start.getDay())) {
        setSaveError('Le bar est ouvert du lundi au vendredi.');
        return;
      }
      const hour = start.getHours();
      if (hour < OPEN_HOUR_START || hour >= OPEN_HOUR_END) {
        setSaveError(`Creneau entre ${OPEN_HOUR_START}h et ${OPEN_HOUR_END}h.`);
        return;
      }
      const size = Number.parseInt(formSize, 10);
      if (Number.isNaN(size) || size < 1) {
        setSaveError('Nombre de personnes invalide.');
        return;
      }
      const cap = formSeat === 'table' ? SEAT_TABLES : SEAT_STOOLS;
      if (countForDay(formDate, formSeat) >= cap) {
        setSaveError(
          formSeat === 'table'
            ? 'Plus de table disponible ce jour-la.'
            : 'Plus de poste disponible ce jour-la.',
        );
        return;
      }
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      await sdk.bookings.create({
        orgId: config.orgId,
        type: 'visit',
        startAt: start,
        endAt: end,
        status: 'confirmed',
        customFields: {
          partySize: size,
          seatKind: formSeat,
          clientName: formName.trim(),
        },
      });
      setShowForm(false);
      setFormName('');
      setFormSize('1');
      setFormSeat('table');
      setFormTime('10:00');
      setFormDate(todayIso());
      await refresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Echec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  }, [sdk, config.orgId, formDate, formTime, formSize, formSeat, formName, countForDay, refresh]);

  /** Ferme le modal de reservation (les champs saisis restent pour la prochaine ouverture). */
  const closeForm = (): void => {
    setShowForm(false);
    setSaveError(null);
  };

  return (
    <ScreenLayout
      /* Zone 1 : identite (icone unique de l'app, regle G1) + bascule chats / visites.
         Pas d'icone dans le menu : la puce en tete du rail suffit (regle G2). */
      appName="Cat Manager Six"
      appIcon={<IconRabbit size={18} />}
      menuItems={[
        { id: 'cats', label: 'Les chats' },
        { id: 'visits', label: 'Les visites' },
      ]}
      activeMenuId={activeTab}
      onMenuSelect={(id) => onTabChange(id as AppTab)}
      /* Zone 2 : en-tete. Le h1 affiche le label de l'onglet actif ("Les
         visites") ; `title` reste le repli. */
      title="Les visites"
      /* Zone 3 : annexe. Stats selectionnees dans les reglages (hub
         Parametres), valeurs toujours calculees des donnees reelles. */
      headerStats={headerStats}
      /* Zone 4 : bascule Aujourd'hui / Cette semaine. Pas de recherche : les
         pastilles seules se rendent (comportement du gabarit). Seules les
         pastilles laissees visibles dans les reglages (pref) s'affichent. */
      filters={rangeFilters}
      activeFilterId={range}
      onFilterSelect={(id) => setRange(id as RangeId)}
      /* Action principale, contrat v2 : `kind: 'add'`, le gabarit impose le
         libelle universel "Ajouter", IconPlus et le lime (regle G5). */
      quickActions={[
        {
          id: 'add-visit',
          label: 'Ajouter',
          kind: 'add',
          disabled: loadingList || listError != null,
          onClick: () => setShowForm(true),
        },
      ]}
      /* Zone 5 : etats standard. Chargement reserve au premier chargement.
         Skeleton sur mesure : jauge (vue Aujourd'hui) + lignes de creneau,
         la vue differe du pattern liste par defaut. */
      loading={loadingList && visits.length === 0}
      skeleton={<ReservationsSkeleton withGauge={range === 'today'} />}
      error={listError}
      errorTitle="Echec du chargement"
      onRetry={() => void refresh()}
      retryLabel="Reessayer"
    >
      {/* Jauge de capacite du jour (uniquement en vue Aujourd'hui). A plat,
          pleine largeur, sans Card (regle G9). */}
      {range === 'today' && (
        <section className="border-b border-border-soft px-5 pb-4 pt-4">
          <h3 className="pb-3 text-sm font-semibold text-ink-900">Capacite du jour</h3>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-ink-900">
                <IconChair2 size={16} className="text-ink-500" />
                <span>
                  Tables : {tablesUsed}/{SEAT_TABLES}
                </span>
              </div>
              <StatusBadge tone={tablesMeta.tone} label={tablesMeta.label} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-ink-900">
                <IconChair2 size={16} className="text-ink-500" />
                <span>
                  Postes : {stoolsUsed}/{SEAT_STOOLS}
                </span>
              </div>
              <StatusBadge tone={stoolsMeta.tone} label={stoolsMeta.label} />
            </div>
            {overflow && (
              <p className="text-xs text-danger">
                Depassement de capacite : plus de reservations que de places pour aujourd'hui.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Liste des creneaux de la periode, ou message si aucun sur la periode. */}
      {statusError != null && <p className="px-5 pt-3 text-xs text-danger">{statusError}</p>}
      {filtered.length === 0 ? (
        <p className="px-5 py-6 text-sm text-ink-500">Aucune visite sur cette periode.</p>
      ) : (
        <ul>
          {filtered.map((visit) => {
            const statusMeta = visitStatusOf(visit.status);
            const busy = statusSavingId === visit.id;
            return (
              <li
                key={visit.id}
                className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <IconStopwatch size={16} className="text-ink-500" />
                    <span className="truncate text-sm font-medium text-ink-900">
                      {range === 'week' ? `${formatDay(visit.startAt)} ` : ''}
                      {formatSlot(visit)}
                    </span>
                  </div>
                  {visit.clientName ? (
                    <span className="truncate pl-6 text-xs text-ink-500">{visit.clientName}</span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-ink-500">
                  <IconChair2 size={14} />
                  <span>{visit.seatKind === 'stool' ? 'Poste' : 'Table'}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-ink-500">
                  <IconUserMultipleCircle size={14} />
                  <span>
                    {visit.partySize} {visit.partySize > 1 ? 'personnes' : 'personne'}
                  </span>
                </div>
                <StatusBadge tone={statusMeta.tone} label={statusMeta.label} />
                <div className="flex shrink-0 gap-1.5">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || visit.status === 'arrived'}
                    onClick={() => void handleVisitStatus(visit.id, 'arrived')}
                  >
                    Arrivee
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy || visit.status === 'cancelled'}
                    onClick={() => void handleVisitStatus(visit.id, 'cancelled')}
                  >
                    Annuler
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Formulaire de nouvelle reservation : pop-up centrale standard,
          jamais en zone 5 (regle G6). */}
      <Modal
        open={showForm}
        onClose={closeForm}
        title="Nouvelle reservation"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeForm}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveVisit()}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="visit-date" className="text-xs font-medium text-ink-500">
                Jour
              </label>
              <Input
                id="visit-date"
                type="date"
                className="h-10"
                value={formDate}
                onChange={(event) => setFormDate(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="visit-time" className="text-xs font-medium text-ink-500">
                Heure
              </label>
              <Input
                id="visit-time"
                type="time"
                className="h-10"
                value={formTime}
                onChange={(event) => setFormTime(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Type de place</span>
            <div className="flex flex-wrap gap-2">
              {SEAT_OPTIONS.map((seat) => (
                <Button
                  key={seat.value}
                  size="sm"
                  variant={seat.value === formSeat ? 'primary' : 'secondary'}
                  onClick={() => setFormSeat(seat.value)}
                >
                  {seat.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="visit-size" className="text-xs font-medium text-ink-500">
                Nombre de personnes
              </label>
              <Input
                id="visit-size"
                type="number"
                min={1}
                className="h-10"
                value={formSize}
                onChange={(event) => setFormSize(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="visit-name" className="text-xs font-medium text-ink-500">
                Nom du client
              </label>
              <Input
                id="visit-name"
                type="text"
                className="h-10"
                value={formName}
                placeholder="Optionnel"
                onChange={(event) => setFormName(event.target.value)}
              />
            </div>
          </div>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>
    </ScreenLayout>
  );
}

export default ReservationsScreen;
